import { useMemo } from 'react';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions';

import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { fg } from '@confluence/feature-gating';
import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { usePageAgentSessionNudges } from '@confluence/page-rovo-config-common/entry-points/usePageAgentSessionNudges.ts';
import type { RovoActivationFragmentRef } from '@confluence/page-rovo-config-common/entry-points/RovoActivation.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import { useConvoStartersNudgeAction } from '@confluence/page-rovo-config-common/entry-points/useConvoStartersNudgeAction.ts';
import {
	isNudgeByAction,
	type NudgeEntry,
} from '@confluence/page-rovo-config-common/entry-points/nudgeTypes.ts';

/** Minimum read time (in minutes) required for the convo starter nudge. */
const CONVO_STARTER_MIN_READ_TIME_MINUTES = 2;

type ViewPageNudgeConfigResult = Readonly<{
	errors: readonly Error[];
	nudges: ConversationAction[];
}>;

type ViewPageNudgeConfigOptions = Readonly<{
	rovoActivationFragmentRef?: RovoActivationFragmentRef | null;
	contentId: string;
	contentType: string;
	isBaseEligible: boolean;
	isCurrentUserContributor: boolean;
	isFirstVisit: boolean;
	isSessionReady: boolean;
	readTimeMinutes: number | null;
}>;

/**
 * Returns the final nudge configuration for the view page: agent-session nudges followed by the
 * existing view-page nudges.
 * Each nudge declares its own enabled condition, including per-nudge read time thresholds.
 * Agent-session eligibility is independent of the base eligibility used by existing nudges.
 */
export function useViewPageNudgeConfig({
	rovoActivationFragmentRef,
	contentId,
	contentType,
	isBaseEligible,
	isCurrentUserContributor,
	isFirstVisit,
	isSessionReady,
	readTimeMinutes,
}: ViewPageNudgeConfigOptions): ViewPageNudgeConfigResult {
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const viewPageRovoActions = useViewPageRovoActions();
	const { nudges: sessionNudges, errors: sessionErrors } = usePageAgentSessionNudges({
		rovoActivationFragmentRef,
		contentId,
		enabled: Boolean(isSessionReady && isRovoButtonEnabled && contentType === 'page' && contentId),
	});
	const convoStartersNudgeAction = useConvoStartersNudgeAction();
	const { eligibleForChangeboarding } = useAudioButtonWrapperStore();

	const actionsById = useMemo(
		() =>
			new Map<string, ConversationAction>(
				viewPageRovoActions.map(
					(registeredAction: { actionId: string; action: ConversationAction }) => [
						registeredAction.actionId,
						registeredAction.action,
					],
				),
			),
		[viewPageRovoActions],
	);

	const baseNudges = useMemo(() => {
		if (!isBaseEligible) return [];

		const nudgeEntries: NudgeEntry[] = [
			{ actionId: 'page-catchup', enabled: true },
			{ actionId: 'audio-briefing', enabled: !eligibleForChangeboarding },
			// Convo starter nudge — requires higher read time than the base threshold
			// Excluded for contributors and first-time visitors
			{
				action: convoStartersNudgeAction,
				enabled:
					!isCurrentUserContributor &&
					!isFirstVisit &&
					readTimeMinutes !== null &&
					readTimeMinutes > CONVO_STARTER_MIN_READ_TIME_MINUTES &&
					// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
					fg('aix_convo_starter_nudge_enabled'),
			},
			// Action items nudge — eligibility gated in useViewPageRovoActions
			{
				actionId: 'action-items-nudge',
				enabled: true,
			},
			{
				actionId: 'status-updates-automation',
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				enabled: fg('cc_automations_discovery_nudges'),
			},
			{
				actionId: 'content-reviewer-nudge',
				enabled: true,
			},
			{
				actionId: 'meeting-notes-nudge',
				enabled: true,
			},
		];

		return nudgeEntries.reduce<ConversationAction[]>((acc, nudgeEntry) => {
			if (!nudgeEntry.enabled) return acc;
			const action = isNudgeByAction(nudgeEntry)
				? nudgeEntry.action
				: actionsById.get(nudgeEntry.actionId);
			if (action) acc.push(action);
			return acc;
		}, []);
	}, [
		isBaseEligible,
		readTimeMinutes,
		eligibleForChangeboarding,
		isCurrentUserContributor,
		isFirstVisit,
		convoStartersNudgeAction,
		actionsById,
	]);

	return useMemo(
		() => ({
			errors: sessionErrors,
			nudges: [...sessionNudges, ...baseNudges],
		}),
		[baseNudges, sessionErrors, sessionNudges],
	);
}
